export interface User {
  userId: string
  email: string
  firstName: string | null
  lastName: string | null
  teamId: string | null
  securityRole: number
  tokenVersion: number
  /** Platform auditor with an active grant: routed to the read-only /audit portal. */
  isAuditor?: boolean
  teamName?: string | null
  /** Set when a Team Admin closed the team: everything but billing, settings and the team page is locked. */
  teamClosedAt?: string | null
  /** For a closed team: the last moment it can be reopened; its data is erased after. */
  teamEraseAfter?: string | null
  /** An account on no team with an invitation waiting. */
  hasPendingInvite?: boolean
}

export interface Template {
  templateId: string
  teamId: string
  folderId: string | null
  name: string
  description: string | null
  templateStatusLookupId: number
  currentVersionId: string | null
  retentionDays: number | null
  legalHold: boolean
  storageRegion: string | null
  createdAt: string
  updatedAt: string
  concurrencyToken: string | null
  /** The version promoted to Live (the one with isActive=true), or null if none is. */
  liveVersionId?: string | null
  /** Only returned by templates/get. */
  pendingPromotion?: PendingPromotion | null
  /** Only returned by templates/get. */
  promotionApproval?: PromotionApprovalRule
  /** Only returned by templates/get. */
  pendingPromotionRequest?: PendingPromotionRequest | null
}

/**
 * auto: promotes at once, recorded as auto-approved. self: raises a request the requester may
 * approve as a separate recorded step. dual: someone else with Manage must approve.
 */
export type ApprovalLevel = 'auto' | 'self' | 'dual'

/** 'default': nothing is set at any level, so the level is 'auto'. */
export type PromotionApprovalSource = 'template' | 'folder' | 'team' | 'default'

/** Effective approval rule: template override, else nearest folder override, else team setting. */
export interface PromotionApprovalRule {
  /** This level's own override; null inherits. */
  setting: ApprovalLevel | null
  level: ApprovalLevel
  /** level !== 'auto' */
  required: boolean
  /** False when the team has fewer than two active members; 'dual' is then refused. */
  dualAvailable: boolean
  dualUnavailableReason: string | null
  source: PromotionApprovalSource
  sourceFolderId: string | null
}

export interface PendingPromotionRequest {
  promotionRequestId: string
  templateVersionId: string
  versionNumber: number
  promoteAt: string | null
  changeNote: string | null
  requestedBy: string
  requestedByEmail: string | null
  requestedAt: string
  approvalLevel: Exclude<ApprovalLevel, 'auto'>
  canApprove: boolean
  canCancel: boolean
}

export type PromotionRequestStatus = 'pending' | 'approved' | 'rejected' | 'cancelled' | 'superseded' | 'expired'

export interface PromotionRequest {
  promotionRequestId: string
  templateId: string
  templateName: string
  templateVersionId: string
  versionNumber: number
  folderId: string | null
  requestedBy: string
  requestedByEmail: string | null
  requestedAt: string
  promoteAt: string | null
  changeNote: string | null
  status: PromotionRequestStatus
  decidedBy: string | null
  decidedAt: string | null
  decisionNote: string | null
  approvalLevel: Exclude<ApprovalLevel, 'auto'>
  canApprove: boolean
}

/** Counts of overrides under a folder or the team that an apply-to-descendants would clear. */
export interface OverridesBelow {
  folders: number
  templates: number
}

export interface PendingPromotion {
  templateVersionId: string
  /** ISO 8601. */
  promoteAt: string
  changeNote: string | null
}

export interface TemplateVersion {
  templateVersionId: string
  templateId: string
  versionNumber: number
  inputFormatLookupId: number
  sourceByteSize: number
  isActive: boolean
  changeNote: string | null
  activatedAt: string | null
  createdAt: string
  concurrencyToken: string | null
}

export interface TemplateVariable {
  normalizedKey: string
  rawName: string
  variableKindLookupId: number
  occurrenceCount: number
  contextSnippet: string | null
  loopKey: string | null
}

export interface Folder {
  folderId: string
  teamId: string
  parentFolderId: string | null
  name: string
  /** Materialized path, "/{folderId}/{folderId}/" root-to-self. Used for descendant checks. */
  path: string
  createdAt: string
  updatedAt: string
  /** Promotion-approval override; null inherits from the parent folder or the team. */
  promotionApproval?: ApprovalLevel | null
}

export interface RenderJob {
  renderJobId: string
  templateVersionId: string
  renderStatusLookupId: number
  outputByteSize: number | null
  errorMessage: string | null
  warningJson: string | null
  durationMs: number | null
  pageCount: number | null
  createdAt: string
  outputFormatLookupId?: number
  outputBlobPath?: string | null
  renderOptionsJson?: string | null
  purgedAt?: string | null
}

export interface BatchRenderResult {
  renderJobId: string
  success: boolean
  errorMessage: string | null
}

export interface ApiKey {
  apiKeyId: string
  name: string
  keyPrefix: string
  isActive: boolean
  capabilities: string
  workspaceId: string | null
  /** Scope: this folder and its descendants. null = the whole workspace. */
  folderId: string | null
  lastUsedAt: string | null
  revokedAt: string | null
  createdAt: string
}

export interface Workspace {
  workspaceId: string
  teamId: string
  name: string
  isTest: boolean
  createdAt: string
}


export interface TeamMember {
  userId: string
  emailAddress: string
  firstName: string | null
  lastName: string | null
  role: number
  status: number
}

export interface AuditEvent {
  auditEventId: string
  teamId: string
  actorUserId: string | null
  eventType: string
  entityType: string | null
  entityId: string | null
  createdAt: string
}

/** One period on the billing history. Mirrors BillingPeriodStatus in lib/src/Data/BillingModels.cs. */
export const BillingPeriodStatus = {
  COMPUTED: 10,
  CHARGING: 20,
  PAID: 30,
  FAILED: 40,
  /** Below the provider's minimum charge; rolled into the next period rather than discarded. */
  CARRIED_FORWARD: 50,
  ZERO: 60,
  /** Owed from an earlier paid plan when the team was assigned Free: billed on the first bill after it returns to a paid plan. */
  DEFERRED: 70,
} as const

/** One balance assigning Free defers (AdminController free-deferral-preview / set-team-plan). */
export interface DeferredBalance {
  kind: 'failed_period' | 'carried_forward' | 'usage_charge'
  billingPeriodId: string | null
  billingUsageChargeId: string | null
  periodStart: string | null
  amountCents: number
  currency: string
  description: string
  /** A carried-forward period whose cents a later period already includes: listed, not billed again. */
  inLaterPeriod: boolean
}

export interface DeferralSummary {
  items: DeferredBalance[]
  totalCents: number
  standingBefore: number
  standingCleared: boolean
}

export interface BillingPeriod {
  periodStart: string
  periodEnd: string
  status: number
  totalCents: number
  currency: string
}

/** The card on file. Display fields only — none of these expands PCI audit scope. */
export interface StoredPaymentMethod {
  brand: string | null
  last4: string | null
  expiryMonth: number | null
  expiryYear: number | null
}

/** Mirrors BillingPlan in lib/src/Data/BillingModels.cs, cheapest first. */
export const BillingPlanCode = {
  FREE: 'free',
  STANDARD: 'standard',
  ENHANCED: 'enhanced',
  ULTIMATE: 'ultimate',
} as const

/** Display names, so one place decides how a plan code is spelled to a customer. */
export const BILLING_PLAN_NAMES: Record<string, string> = {
  [BillingPlanCode.FREE]: 'Free',
  [BillingPlanCode.STANDARD]: 'Standard',
  [BillingPlanCode.ENHANCED]: 'Enhanced',
  [BillingPlanCode.ULTIMATE]: 'Ultimate',
}

export interface BillingStatus {
  /** Whether the payment provider is configured in this environment. When false, the billing page
   *  shows "not configured" instead of a card form that would 503. */
  billingAvailable: boolean
  planCode: string
  /** An outstanding balance could not be collected; rendering is refused until it clears. */
  isSuspended: boolean
  hasPaymentMethod: boolean
  /** Whether this plan needs a card at all. Free does not. */
  requiresPaymentMethod: boolean
  /** A team that needs a card and has none (card gate): plan requires one, not exempt, none on file. */
  needsPaymentMethod: boolean
  /** Chargeable work is barred — suspended, or needs a card. */
  workBarred: boolean
  /** When the 30-day free trial ends, or null when not trialing. */
  trialEndsAt: string | null
  /** Whether the free trial is currently running. */
  isTrialing: boolean
  /** Documents included this month. Set on Free only; the paid plans cap pages instead. */
  includedDocuments: number | null
  /** Pages included in the monthly minimum, or null when the plan prices every page. */
  includedPages: number | null
  /** The plan's minimum monthly cost, in whole cents. */
  monthlyMinimumCents: number | null
  /** Cents per page beyond the allowance. Fractional — Enhanced's is 0.89. */
  perPageCents: number | null
  /** Usage past the included quantity is billed rather than refused. */
  allowsOverage: boolean
  /** Documents rendered this period. What Free's cap counts. */
  usedThisPeriod: number
  /** Pages rendered this period. What every paid plan's allowance counts. */
  pagesThisPeriod: number
  /** When this team asked for a negotiated rate their volume has outgrown Ultimate's. */
  customPricingRequestedAt: string | null
  /** Whether pages past the included allowance are billed (default) or refused. */
  allowOverage?: boolean
  /** The monthly page-overage limit in whole dollars (null = no limit); used only while allowOverage is on. Kept while off. */
  overageLimitDollars?: number | null
  /** Whether the overage settings apply: false for Free, billing-exempt and allowance-less plans. */
  appliesOverageSettings?: boolean
  /** Overage is off and applies: renders stop at the included pages. */
  stopsAtIncludedPages?: boolean
  /** The most pages this month may reach under the settings, or null for no ceiling. */
  pageCeiling?: number | null
  /** Team Admin or Manage billing: may change billing settings such as the overage settings. */
  canManageBilling?: boolean
  /** Max documents this team can have rendering at once (synchronous), across every server: the plan
   *  default or a negotiated override. */
  concurrentRenderLimit?: number
  paymentMethod: StoredPaymentMethod | null
  periods: BillingPeriod[]
}

export interface AdminTeam {
  teamId: string
  name: string
  createdAt: string
  userCount?: number
  /** Billing standing, joined into the listing — the console's whole job here. */
  planCode: string
  /** Not a customer: not billed, and no card required. Internal teams and our own account. */
  billingExempt: boolean
  exemptReason: string | null
  /** When this team asked for pricing we do not publish, or null. */
  customPricingRequestedAt: string | null
  /** 10 ok, 20 past due, 30 suspended — see BillingStatus server-side. */
  billingStatus: number
  /** When its Team Admin closed it (V085); null while open. */
  closedAt?: string | null
  /** End of the reopen window; the job erases it after. */
  eraseAfter?: string | null
  /** When the closed team was erased (only the anonymized shell remains). */
  erasedAt?: string | null
}

export interface AdminUser {
  userId: string
  email: string
  firstName: string | null
  lastName: string | null
  teamId: string | null
  securityRole: number
  isLocked: boolean
  isDeactivated?: boolean
  isInvited?: boolean
  createdAt: string
  lastLoginAt: string | null
}

export type AdminUserRoleFilter = 'super-admin' | 'team-admin' | 'member'
export type AdminUserStatusFilter = 'active' | 'locked' | 'invited' | 'deactivated'
export interface AdminUserFilters {
  role?: AdminUserRoleFilter
  status?: AdminUserStatusFilter
  query?: string
}

export interface AdminCounts {
  teams: number
  users: number
  superAdmins: number
  teamAdmins: number
  lockedUsers: number
  invitedUsers: number
  complianceTasksDue: number
  openAuditorRequests: number
}

export interface PagedResult<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
}

export interface DemoTemplate {
  templateId: string
  name: string
  description: string | null
  currentVersionId: string | null
}

export type PayloadFormat = 'json' | 'xml'

// ── Global search ─────────────────────────────────────────────────────────

export interface SearchTemplateHit {
  templateId: string
  name: string
  status: string
  version: number | null
  folderPath: string | null
  updatedAt: string
}

export interface SearchFolderHit {
  folderId: string
  name: string
  path: string
  updatedAt: string
}


export interface SearchUserHit {
  userId: string
  email: string
  fullName: string | null
  role: string   // "Admin" | "Editor" | "Viewer"
  status: string // "Active" | "Invited" | "Suspended"
  lastSignInAt: string | null
}

export interface GlobalSearchResult {
  templates: SearchTemplateHit[]
  folders: SearchFolderHit[]
  users: SearchUserHit[]
}

/** One row of doc.log_entry, as the admin log viewer reads it. */
export interface LogEntry {
  logEntryId: string
  message: string | null
  messageTemplate: string | null
  level: string | null
  timeStamp: string | null
  exception: string | null
  properties: string | null
  userId: string | null
  teamId: string | null
  httpMethod: string | null
  url: string | null
  httpStatus: number | null
  /** Set on lines written by a background job (a JobCatalog name, or RenderWorker). */
  jobName: string | null
  jobRunId: string | null
}

export interface LogQuery {
  page?: number
  pageSize?: number
  /** Serilog's spelling — "Warning", "Error". Null/absent means every level. */
  minimumLevel?: string | null
  since?: string | null
  search?: string | null
  minimumHttpStatus?: number | null
  /** 'requests' | 'jobs' | null (everything). */
  source?: 'requests' | 'jobs' | null
  jobName?: string | null
  jobRunId?: string | null
}

export type JobStatus = 'running' | 'succeeded' | 'partial' | 'failed' | 'never'

export interface AdminJob {
  name: string
  title: string
  summary: string
  description: string
  intervalSeconds: number
  status: JobStatus
  runningOn: string | null
  lastStartedAt: string | null
  lastFinishedAt: string | null
  lastOutcome: string | null
  lastDurationMs: number | null
  lastSummary: string | null
  lastError: string | null
  lastRunId: string | null
  nextDueAt: string | null
}

export interface RenderQueueStats {
  pending: number
  inProgress: number
  completed24h: number
  failed24h: number
  oldestPendingAt: string | null
}

export interface AdminJobsResponse {
  generatedAt: string
  jobs: AdminJob[]
  renders: RenderQueueStats
}

